import React, { useEffect, useMemo, useState } from "react";

import Layout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import Tabs from "src/components/ui/Tabs";
import Card from "src/components/common/card/Card";
import BarChart from "src/components/ui/chart/BarChart";
import adminReportsApis from "src/api/adminReports";
import {
  AdminReportsResponse,
  AdminReportsOperations,
} from "src/types/adminReports.types";
import { currencyFormatter } from "src/utils/common";

const AdminReportsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [reportData, setReportData] = useState<AdminReportsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tabs = useMemo(
    () => [
      { label: "Overview", value: "overview" },
      { label: "Revenue", value: "revenue" },
      { label: "Tenants", value: "tenants" },
      { label: "Operations", value: "operations" },
    ],
    []
  );

  useEffect(() => {
    const loadReports = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = (await adminReportsApis.getSummary({
          days: 7,
        })) as AdminReportsResponse;
        setReportData(response);
      } catch (err: any) {
        setError(err?.message || "Failed to load reports");
      } finally {
        setLoading(false);
      }
    };

    loadReports();
  }, []);

  const revenueSeries = useMemo(
    () => reportData?.revenue?.salesSeries || [],
    [reportData?.revenue?.salesSeries]
  );

  const billingSeries = useMemo(
    () => reportData?.revenue?.purchaseSeries || [],
    [reportData?.revenue?.purchaseSeries]
  );

  const topIndustries = useMemo(
    () => reportData?.revenue?.topTenants || [],
    [reportData?.revenue?.topTenants]
  );

  const overviewCards = useMemo(() => {
    const overview = reportData?.overview;
    return [
      {
        label: "Active Tenants",
        value: overview?.activeTenants ?? 0,
      },
      {
        label: "Monthly Recurring Revenue",
        value: currencyFormatter(overview?.monthlyRecurringRevenue || 0),
      },
      {
        label: "Avg. Plan Utilization",
        value:
          typeof overview?.avgPlanUtilization === "number"
            ? `${overview.avgPlanUtilization}%`
            : "N/A",
      },
      {
        label: "Support Requests (30d)",
        value: overview?.supportRequestsLast30Days ?? 0,
      },
    ];
  }, [reportData?.overview]);

  const highUtilization = reportData?.tenants?.highUtilization || [];
  const planDistribution = reportData?.tenants?.planDistribution || [];
  const operations: AdminReportsOperations | null = reportData?.operations || null;

  return (
    <Layout
      type="admin"
      meta={{
        title: "Reports | Invyntra",
        description: "Monitor platform-wide revenue, tenants, and operations.",
        keywords: "reports, admin, analytics, invyntra",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title="Reports"
          breadcrumbData={[
            { label: "Home", path: "/admin/dashboard" },
            { label: "Reports", path: "/admin/reports" },
          ]}
        />

        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={setActiveTab}
          className="flex flex-wrap gap-2 border-b"
        />

        {error && (
          <Card>
            <p className="text-sm text-red-600">{error}</p>
          </Card>
        )}

        {activeTab === "overview" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {overviewCards.map((item) => (
                <Card key={item.label}>
                  <p className="text-sm text-gray-500">{item.label}</p>
                  <p className="text-2xl font-semibold text-gray-800 mt-2">
                    {item.value}
                  </p>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <p className="text-sm text-gray-600 font-medium">
                  Revenue Trend
                </p>
                {loading ? (
                  <p className="mt-4 text-sm text-gray-500">Loading...</p>
                ) : revenueSeries.length === 0 ? (
                  <p className="mt-4 text-sm text-gray-500">
                    No revenue data available.
                  </p>
                ) : (
                  <BarChart
                    data={revenueSeries}
                    dataKey="totalSales"
                    xKey="date"
                    hideXAxis
                    hideYAxis
                    barColor="#A5B4FC"
                    barRadius={[8, 8, 8, 8]}
                    tooltipFormatter={(value?: number) =>
                      currencyFormatter(value || 0, "IN", 2)
                    }
                  />
                )}
              </Card>
              <Card>
                <p className="text-sm text-gray-600 font-medium">
                  Billing Volume
                </p>
                {loading ? (
                  <p className="mt-4 text-sm text-gray-500">Loading...</p>
                ) : billingSeries.length === 0 ? (
                  <p className="mt-4 text-sm text-gray-500">
                    No billing data available.
                  </p>
                ) : (
                  <BarChart
                    data={billingSeries}
                    dataKey="totalPurchases"
                    xKey="date"
                    hideXAxis
                    hideYAxis
                    barColor="#93C5FD"
                    barRadius={[8, 8, 8, 8]}
                    tooltipFormatter={(value?: number) =>
                      currencyFormatter(value || 0, "IN", 2)
                    }
                  />
                )}
              </Card>
            </div>
          </div>
        )}

        {activeTab === "revenue" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <p className="text-sm text-gray-600 font-medium">
                Weekly Revenue
              </p>
              {loading ? (
                <p className="mt-4 text-sm text-gray-500">Loading...</p>
              ) : revenueSeries.length === 0 ? (
                <p className="mt-4 text-sm text-gray-500">
                  No revenue data available.
                </p>
              ) : (
                <BarChart
                  data={revenueSeries}
                  dataKey="totalSales"
                  xKey="date"
                  hideXAxis
                  hideYAxis
                  barColor="#A5B4FC"
                  barRadius={[8, 8, 8, 8]}
                  tooltipFormatter={(value?: number) =>
                    currencyFormatter(value || 0, "IN", 2)
                  }
                />
              )}
            </Card>
            <Card>
              <p className="text-sm text-gray-600 font-medium">
                Top Revenue Tenants
              </p>
              {loading ? (
                <p className="mt-4 text-sm text-gray-500">Loading...</p>
              ) : topIndustries.length === 0 ? (
                <p className="mt-4 text-sm text-gray-500">
                  No tenant revenue data available.
                </p>
              ) : (
                <BarChart
                  data={topIndustries}
                  dataKey="totalSales"
                  xKey="name"
                  containerClassName="mt-4 h-52 w-full"
                  hideYAxis
                  barColor="#86EFAC"
                  barRadius={[8, 8, 8, 8]}
                  xAxisProps={{
                    tick: { fontSize: 11 },
                    interval: 0,
                    angle: -20,
                    textAnchor: "end",
                    height: 50,
                  }}
                  tooltipFormatter={(value?: number) =>
                    currencyFormatter(value || 0, "IN", 2)
                  }
                />
              )}
            </Card>
          </div>
        )}

        {activeTab === "tenants" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <p className="text-sm text-gray-600 font-medium">
                High Utilization Tenants
              </p>
              <ul className="mt-4 space-y-3 text-sm text-gray-700">
                {loading ? (
                  <li className="text-sm text-gray-500">Loading...</li>
                ) : highUtilization.length === 0 ? (
                  <li className="text-sm text-gray-500">
                    No utilization data available.
                  </li>
                ) : (
                  highUtilization.map((item) => (
                    <li
                      key={`${item.tenantName}-${item.utilizationPercent}`}
                      className="flex items-center gap-2"
                    >
                      <span className="h-2 w-2 rounded-full bg-emerald-400" />
                      {`${item.tenantName} - ${item.utilizationPercent}% plan usage`}
                    </li>
                  ))
                )}
              </ul>
            </Card>
            <Card>
              <p className="text-sm text-gray-600 font-medium">
                Plan Distribution
              </p>
              <ul className="mt-4 space-y-3 text-sm text-gray-700">
                {loading ? (
                  <li className="text-sm text-gray-500">Loading...</li>
                ) : planDistribution.length === 0 ? (
                  <li className="text-sm text-gray-500">
                    No plan distribution data available.
                  </li>
                ) : (
                  planDistribution.map((item) => (
                    <li key={item.planName} className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-indigo-400" />
                      {`${item.planName}: ${item.tenantCount} tenants`}
                    </li>
                  ))
                )}
              </ul>
            </Card>
          </div>
        )}

        {activeTab === "operations" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <p className="text-sm text-gray-600 font-medium">
                Support Resolution
              </p>
              <div className="mt-4 space-y-3 text-sm text-gray-700">
                <div className="flex items-center justify-between">
                  <span>Avg. first response</span>
                  <span className="font-semibold">
                    {typeof operations?.avgFirstResponseHours === "number"
                      ? `${operations.avgFirstResponseHours}h`
                      : "N/A"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Avg. resolution</span>
                  <span className="font-semibold">
                    {typeof operations?.avgResolutionHours === "number"
                      ? `${operations.avgResolutionHours}h`
                      : "N/A"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Open requests</span>
                  <span className="font-semibold">
                    {operations?.openRequests ?? 0}
                  </span>
                </div>
              </div>
            </Card>
            <Card>
              <p className="text-sm text-gray-600 font-medium">
                Platform Health
              </p>
              <div className="mt-4 space-y-3 text-sm text-gray-700">
                <div className="flex items-center justify-between">
                  <span>API uptime (30d)</span>
                  <span className="font-semibold">
                    {typeof operations?.platformHealth?.uptimePercent === "number"
                      ? `${operations.platformHealth.uptimePercent}%`
                      : "N/A"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Failed syncs</span>
                  <span className="font-semibold">
                    {operations?.platformHealth?.failedSyncs ?? 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Average API latency</span>
                  <span className="font-semibold">
                    {typeof operations?.platformHealth?.avgLatencyMs === "number"
                      ? `${operations.platformHealth.avgLatencyMs}ms`
                      : "N/A"}
                  </span>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminReportsPage;
